import React, { useCallback, useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import { Box, Typography, Button, FormControl, MenuItem, Select } from "@mui/material";
import axios from "axios";
import { useGlobalContext } from "../../context/global";
import StopLossLogsModal from "./subComponents/StopLossLogsModal/StopLossLogsModal";
import StopLossTable from "./subComponents/StopLossTable";
import { toast } from "react-toastify";
import CustomPagination from "../../components/CustomPagination/CustomPagination";

const PAGE_SIZE_OPTIONS = [50, 100, 200, 300, 500];

const StopLossList = () => {
  const dashboardSideBarVisibiltyState = useSelector(
    (state) => state.dashboardData.dashboardSideBarVisibiltyState
  );
  const { clientId } = useGlobalContext();
  const userPlatform = JSON.parse(localStorage.getItem("user_platform"));
  const [isFetchingData, setIsFetchingData] = useState(true);
  const [showLogsModal, setShowLogsModal] = useState("");
  const [ruleList, setRuleList] = useState([]);
  const [updatingRuleIds, setUpdatingRuleIds] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageLimit, setPageLimit] = useState(50);
  const [totalCount, setTotalCount] = useState(0);

  const STOP_LOSS_RULE_TYPE = {
    amazon: "AMAZON_STOP_LOSS",
    flipkart: "FLIPKART_STOP_LOSS",
    myntra: "MYNTRA_STOP_LOSS",

  };

  const navigate = useNavigate();

  function capitalizeFirstLetter(string) {
    if (!string) return "";
    return string.charAt(0).toUpperCase() + string.slice(1);
  }

  const handleRedirectForUpdate = (dataId) => {
    const url = `/product-analytics?reportType=child_product&viewtype=PERFORMANCE&page=stoploss&pslRuleId=${dataId}`;
    const stopLossData = ruleList?.find((ruleSet) => ruleSet?.id === dataId);
    navigate(url, { state: { stopLossData } });
  };

  async function handleUpdateRuleStatus(pslRuleId, status) {
    const newStatus = status ? "Active" : "Inactive";

    setUpdatingRuleIds((prev) => [...prev, pslRuleId]);

    try {
      const { data } = await axios.patch(
        `${process.env.REACT_APP_ADS_API}/client/${clientId}/automation-rule-set/${pslRuleId}?source=${userPlatform}&type=${STOP_LOSS_RULE_TYPE[`${userPlatform.toLowerCase()}`]}`,
        { status: newStatus }
      );

      if (data?.success) {
        setRuleList((prev) =>
          prev.map((rule) =>
            rule.id === pslRuleId ? { ...rule, status: newStatus } : rule
          )
        );
        toast.success(`${newStatus === "Active" ? "Stoploss Rule enabled" : "Stoploss Rule disabled"} successfully!`);
      } else {
        toast.error("Error while saving Stoploss Rule. Try Again!");
      }
    } catch (error) {
      console.log("Error while updating Stoploss Rule", error);
      toast.error("Error while saving Stoploss Rule. Try Again!");
    } finally {
      setUpdatingRuleIds((prev) => prev.filter((id) => id !== pslRuleId));
    }
  }

  const ruleType = STOP_LOSS_RULE_TYPE[userPlatform?.toLowerCase()];

  const fetchRuleList = useCallback(async () => {
    if (!clientId || !ruleType) {
      setRuleList([]);
      setIsFetchingData(false);
      return;
    }

    setIsFetchingData(true);
    try {
      const { data } = await axios.get(
        `${process.env.REACT_APP_ADS_API}/client/${clientId}/automation-rule-set?source=${userPlatform}&type=${ruleType}&order_by=name&sorting_order=asc&page=${currentPage - 1}&limit=${pageLimit}`
      );
      const list = Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : [];
      const filteredList = list.filter((rule) => rule?.type === ruleType);
      const totalFromApi = data?.total_count ?? null;

      setRuleList(filteredList);
      if (totalFromApi !== null && totalFromApi !== undefined) {
        setTotalCount(totalFromApi);
      } else if (filteredList.length < pageLimit) {
        setTotalCount((currentPage - 1) * pageLimit + filteredList.length);
      } else {
        setTotalCount((currentPage - 1) * pageLimit + filteredList.length + 1);
      }
    } catch (error) {
      console.log("Error while fetching Stoploss Rules", error);
      toast.error("Error while fetching Stoploss Rules. Try Again!", {
        toastId: "fetchStopLossRulesError",
      });
      setRuleList([]);
    } finally {
      setIsFetchingData(false);
    }
  }, [clientId, userPlatform, ruleType, currentPage, pageLimit]);

  useEffect(() => {
    fetchRuleList();
  }, [fetchRuleList]);

  useEffect(() => {
    setCurrentPage(1);
  }, [clientId, userPlatform]);

  const totalPages = Math.max(1, Math.ceil(totalCount / pageLimit));
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };
  const handlePageLimitChange = (event) => {
    setPageLimit(Number(event.target.value));
    setCurrentPage(1);
  };

  return (
    <div
      className={
        dashboardSideBarVisibiltyState
          ? "main-content-container-onopen-mode"
          : "main-content-container-oncolapse-mode"
      }
      id="main-box"
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h6" fontWeight={600}>
            Stop Loss
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage stop loss rules for your products on{" "}
            {capitalizeFirstLetter(userPlatform)}
          </Typography>
        </Box>

        <Button
          onClick={() =>
            navigate(
              "/product-analytics?reportType=child_product&viewtype=PERFORMANCE&page=stoploss"
            )
          }
          variant="contained"
          startIcon={<AddIcon />}
          sx={{
            borderRadius: "8px",
            textTransform: "none",
            backgroundColor: "#6759FF",
            boxShadow: "none",
            "&:hover": {
              boxShadow: "none",
              backgroundColor: "#5648e6",
            },
          }}
        >
          Create Stop Loss
        </Button>
      </Box>

      <StopLossTable
        ruleList={ruleList}
        isLoading={isFetchingData}
        updatingRuleIds={updatingRuleIds}
        onStatusChange={handleUpdateRuleStatus}
        onEdit={handleRedirectForUpdate}
        onLogs={setShowLogsModal}
      />

      {!isFetchingData && (totalCount > 0 || currentPage > 1) && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            py: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              color: "#4f5366",
            }}
          >
            <Typography variant="body2" sx={{ fontSize: "12px" }}>
              Page Size
            </Typography>
            <FormControl size="small" sx={{ minWidth: 80 }}>
              <Select
                value={pageLimit}
                onChange={handlePageLimitChange}
                disabled={isFetchingData}
                sx={{
                  height: 36,
                  fontSize: "12px",
                  color: "#333333",
                }}
                MenuProps={{ disableScrollLock: true }}
              >
                {PAGE_SIZE_OPTIONS.map((option) => (
                  <MenuItem
                    key={option}
                    value={option}
                    sx={{ fontSize: "12px" }}
                  >
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <CustomPagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            lazyLoading={isFetchingData}
            totalItems={totalCount}
            itemsPerPage={pageLimit}
            hidePagination={!totalCount}
          />
        </Box>
      )}

      <StopLossLogsModal
        showLogsModal={showLogsModal}
        pageName="stoploss"
        setShowLogsModal={setShowLogsModal}
      />
    </div>
  );
};

export default StopLossList;
